import { Component, State, Listen, h, Prop, EventEmitter, Event, Method } from '@stencil/core';
import { breakpoints } from '../../utils/variables';
import { getInternalFormatFromDate } from '../../utils/dates';

// Number of months to display for mobile and desktop
const MOBILE_MONTHS_NB = 1;
const DESKTOP_MONTHS_NB = 2;

/**
 * A date picker present a calendar to the user making it easy to select a date.
 */

@Component({
  tag: 'som-date-picker',
  styleUrl: 'som-date-picker.scss',
})
export class SomDatePicker {
  resizeTimeout;

  updateWidthCb;

  calendarEl: HTMLSomCalendarElement | HTMLSomCalendarVerticalElement;

  @State() monthsNb = DESKTOP_MONTHS_NB;

  /**
   * This property shows the current date selected. You can use this property to set a default date.
   */
  @Prop({ mutable: true, reflect: true }) date: string = '';

  /**
   * This property shows the minimum date that can be selected.
   */
  @Prop() minDate: string = '';

  /**
   * This property shows the maximum date that can be selected.
   */
  @Prop() maxDate: string = '';

  /**
   * When set to `true`, this boolean makes the date picker read only.
   */
  @Prop() readonly = false;

  /**
   * This boolean switches the date picker to vertical mode, which lets users go through months by a top-down format.
   */
  @Prop() vertical = false;


  /**
   * This boolean enables viewing only one month at a time. Display only one month at a time.
   */
  @Prop() singleMonthView: boolean = false;

  /**
   * This event is emitted whenever a new date is selected.
   * @Returns `event.detail` will contain the current selected date.
   */
  @Event() dateChange: EventEmitter;

  componentWillLoad() {
    this.updateWidthCb = this.updateWidth.bind(this);
    this.updateWidth();
  }

  @Listen('resize', { target: 'window' })
  handleWindowResize() {
    clearTimeout(this.resizeTimeout);

    // Setup debounce so we're not constantly reading screen width
    this.resizeTimeout = setTimeout(this.updateWidthCb, 300);
  }

  /**
   * This method resets the date picker.
   * @returns Promise when date picker has been reset.
   */
  @Method() async reset(): Promise<void> {
    return this.calendarEl.reset();
  }

  /**
   * This method sets the focus onto the date picker.
   * @returns Promise when focus has been set.
   */
  @Method() async setFocus(): Promise<void> {
    return this.calendarEl.setFocus();
  }

  updateWidth() {
    if (this.singleMonthView) {
      this.monthsNb = 1;
    } else {
      this.monthsNb = (window.innerWidth < breakpoints.lg) ? MOBILE_MONTHS_NB : DESKTOP_MONTHS_NB;
    }
  }

  handleDateChange(e: CustomEvent) {
    e.stopPropagation();

    this.date = e.detail.date1 ? getInternalFormatFromDate(e.detail.date1) : '';

    this.dateChange.emit(e.detail.date1);
  }

  render() {
    return this.vertical ? (
      <som-calendar-vertical
        ref={ref => {
          this.calendarEl = ref;
        }}
        enableRangePicker={false}
        readonly={this.readonly}
        date1={this.date} // Expects MM/DD/YYY format
        minDate={this.minDate} // Expects MM/DD/YYY format
        maxDate={this.maxDate} // Expects MM/DD/YYY format
        onDateChange={this.handleDateChange.bind(this)}
      ></som-calendar-vertical>
    ) : (
      <som-calendar
        ref={ref => {
          this.calendarEl = ref;
        }}
        enableRangePicker={false}
        monthsNb={this.monthsNb}
        readonly={this.readonly}
        date1={this.date} // Expects MM/DD/YYY format
        minDate={this.minDate} // Expects MM/DD/YYY format
        maxDate={this.maxDate} // Expects MM/DD/YYY format
        onDateChange={this.handleDateChange.bind(this)}
      ></som-calendar>
    );
  }
}
